import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Car, ClipboardCheck, AlertTriangle, ShieldAlert } from "lucide-react";
import { PageHeader, Kpi, Panel, Loading, Empty, DriverStatus, LevelBadge, useFetch } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChecksTable } from "@/pages/livraison/LivDashboard";
import { PeriodsTable, AnomalyTable } from "@/pages/livraison/VehicleFiche";
import { AccidentsTable } from "@/pages/livraison/Accidents";
import { LicencePanel } from "@/components/Documents";
import { fDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ChauffeurFiche() {
  const { id } = useParams();
  const { data, loading, error } = useFetch(`/drivers/${id}/fiche`);
  const { user } = useAuth();
  const acc = useFetch("/accidents", { driver_id: id });
  if (loading) return <Loading />;
  if (!data) return <Empty text={error || "Chauffeur introuvable"} />;
  const d = data.driver;
  const lc = data.last_check;
  return (
    <div data-testid="driver-fiche">
      <Link to="/livraison/chauffeurs" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0E2D43] mb-4" data-testid="back-to-drivers">
        <ArrowLeft className="w-4 h-4" /> Chauffeurs
      </Link>
      <PageHeader overline="Chauffeur de livraison" title={d.name}><DriverStatus v={d.status} /></PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <Kpi testid="driver-last-vehicle" label="Dernier véhicule utilisé" value={lc?.plate || "—"} sub={lc ? `Le ${fDate(lc.date)}` : "Aucune utilisation"} icon={Car} tone="navy" />
        <Kpi testid="driver-vehicles-count" label="Véhicules utilisés" value={data.vehicles.length} icon={Car} delay={1} />
        <Kpi testid="driver-checks-count" label="Contrôles" value={data.checks.length} icon={ClipboardCheck} delay={2} />
        <Kpi testid="driver-anomalies-count" label="Anomalies / dommages" value={`${data.anomalies.length} / ${data.damages.length}`} icon={AlertTriangle} tone="danger" delay={3} />
      </div>
      <Tabs defaultValue="periods">
        <TabsList className="flex-wrap h-auto mb-3">
          <TabsTrigger value="periods" data-testid="driver-tab-periods">Véhicules utilisés</TabsTrigger>
          <TabsTrigger value="checks" data-testid="driver-tab-checks">Contrôles</TabsTrigger>
          <TabsTrigger value="anomalies" data-testid="driver-tab-anomalies">Anomalies</TabsTrigger>
          <TabsTrigger value="damages" data-testid="driver-tab-damages">Dommages</TabsTrigger>
          <TabsTrigger value="accidents" data-testid="driver-tab-accidents">Accidents ({acc.data?.length ?? 0})</TabsTrigger>
          {user.role !== "chauffeur" && <TabsTrigger value="licence" data-testid="driver-tab-licence">Permis de conduire</TabsTrigger>}
        </TabsList>
        <Panel flush>
          <TabsContent value="periods" className="m-0"><PeriodsTable rows={data.vehicle_periods} label="Véhicule" testid="driver-vehicle-periods" link={(k) => `/livraison/vehicules/${k}`} /></TabsContent>
          <TabsContent value="checks" className="m-0"><ChecksTable checks={data.checks} /></TabsContent>
          <TabsContent value="anomalies" className="m-0"><AnomalyTable items={data.anomalies} /></TabsContent>
          <TabsContent value="licence" className="m-0"><LicencePanel ownerType="driver" ownerId={id} /></TabsContent>
          <TabsContent value="accidents" className="m-0"><AccidentsTable rows={acc.data || []} admin={user.role === "admin" && user.real_role !== "chef_depot"} showDriver={false} /></TabsContent>
          <TabsContent value="damages" className="m-0">
            {!data.damages.length ? <Empty text="Aucun dommage constaté" /> : (
              <table className="tbl w-full" data-testid="driver-damages-table"><thead><tr><th>Date</th><th>Véhicule</th><th>Niveau</th><th>Contrôlé par</th><th>Commentaire</th></tr></thead>
                <tbody>{data.damages.map((c) => (
                  <tr key={c.id}><td>{fDate(c.date)}</td><td>{c.plate}</td><td><LevelBadge v={c.dommages} /></td><td>{c.controller_name}</td><td className="whitespace-normal">{c.comment || "—"}</td></tr>
                ))}</tbody></table>
            )}
          </TabsContent>
        </Panel>
      </Tabs>
    </div>
  );
}
